// Pre-route (spec §3.12, Figures 29–31): orders routed before the A2000 pick ticket exists. People
// type the A2000 order # (CTRL_NO) — or a customer PO, and tick its orders — with the routing ref,
// pickup appointment and notes; each moves to its Worklist lines by itself once its pick ticket is in
// the WMS (`pre-route-mover.tsx`). Only the sources of `PRE_ROUTE_ADD_SOURCES` are offered (A2000:
// every open Deposco SO is a Worklist line); a Deposco pre-route from before still moves once its
// line is on the Worklist. The rules are `shared/src/routing/pre-route.ts`; every write goes
// through the writer's kit (`logged-write.ts`).

import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { ArrowRightIcon, PlusIcon, SearchCheckIcon, Trash2Icon, TriangleAlertIcon } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  NO_HOLIDAYS,
  PAGES,
  PRE_ROUTES,
  PRE_ROUTE_ADD_SOURCES,
  PRE_ROUTE_TABS,
  checkAppointment,
  preRouteChoices,
  defaultTicks,
  ticksAfterAdd,
  divisionNamer,
  fieldChange,
  formatDay,
  formatShortDay,
  inPreRouteTab,
  inSource,
  NO_PTS,
  localToday,
  orderSnapshot,
  parseDateInput,
  preRouteAlertCount,
  preRouteMatches,
  preRouteView,
  preRouteViews,
  isMyPreRoute,
} from "@app/shared";
import type {
  DivisionNamer,
  OrderRow,
  PreRouteChoice,
  PreRouteOrderMatches,
  PreRouteRecord,
  PreRouteSource,
  PreRouteTab,
  PreRouteView,
} from "@app/shared";
import { Button, DataError, Input, RegisteredPage, TableSkeleton, useMe } from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import { SavingMark, lineStateClass } from "./chips";
import { useTableColumns } from "./column-drag";
import { PRE_ROUTES_KEY, lookupOrder, useHolidays, usePreRoutes } from "./data";
import { MessageProvider, notify, useMessage } from "./message";
import { NamedText } from "./named";
import { DateField } from "./pickers";
import { usePreRouteAskAi } from "./axai-pre-route";
import { AskAiBar } from "./ask-ai-bar";
import { ColumnsMenu, FilterPill, SearchBox } from "./toolbar";
import { MyCustomersChip, useMyCustomers } from "./my-customers";
import { SourceBadge, SourcePicker, useSourceFilter } from "./source-filter";
import { lostToAnother, useWriter } from "./writer";
import { errorWords, toastIfReadOnly } from "./write-error";
import type { PageSearch } from "../../pages";

/** The pre-routes as the page and the badge read them; `enabled` false for someone with no Role. */
export function usePreRouteViews(enabled = true) {
  const today = useMemo(() => localToday(), []);
  const data = usePreRoutes(enabled);
  const holidays = useHolidays(enabled);
  const calendar = holidays.data?.calendar ?? NO_HOLIDAYS;
  const views = useMemo(
    () => (data.data ? preRouteViews(data.data, today, calendar) : []),
    [data.data, today, calendar],
  );
  return { data, views, today, calendar, degraded: !!holidays.data?.degraded };
}

/** The red badge's count on every page: the pre-routes alerting (spec §3.12). */
export function usePreRouteAlerts(enabled: boolean): number {
  return preRouteAlertCount(usePreRouteViews(enabled).views);
}

const isPreRouteTab = (t: unknown): t is PreRouteTab => PRE_ROUTE_TABS.some((x) => x.id === t);

export function PreRoutePage({ tab }: { tab?: string }) {
  return (
    <RegisteredPage id="pre-route">
      <MessageProvider>
        <PreRouteScreen initialTab={isPreRouteTab(tab) ? tab : "waiting"} />
      </MessageProvider>
    </RegisteredPage>
  );
}

/**
 * The table's columns (Figures 30–31), the Columns menu orders them; Order value is managers' only.
 * Qty and Order value are the order's open ones until it has pick tickets, then theirs; a Moved
 * one's Qty is its lines' copies once A2000 drops them (`preRouteView`).
 */
const COLUMNS = [
  { id: "alert", header: "Alert", className: "w-56" },
  { id: "source", header: "Source", className: "w-20" },
  { id: "orderNo", header: "Order #", className: "w-24" },
  { id: "customer", header: "Customer", className: "w-40" },
  { id: "po", header: "PO", className: "w-28" },
  { id: "division", header: "Division", className: "w-[90px]" },
  { id: "dc", header: "DC", className: "w-32" },
  { id: "qty", header: "Qty", className: "w-16 text-right" },
  { id: "start", header: "Start", className: "w-16" },
  { id: "cancel", header: "Cancel", className: "w-16" },
  { id: "appointment", header: "Pickup appt", className: "w-28" },
  { id: "routingRef", header: "Routing ref / load ID", className: "w-40" },
  { id: "notes", header: "Notes", className: "min-w-56" },
  { id: "wh", header: "WH", className: "w-12" },
  { id: "movedTo", header: "Moved to", className: "w-28" },
  { id: "movedAt", header: "Moved at", className: "w-24" },
  { id: "value", header: "Order value", className: "w-24 text-right" },
];

type Editable = "routingRef" | "appointmentDate" | "notes";
const LABEL: Record<Editable, string> = {
  routingRef: "Routing ref / load ID",
  appointmentDate: "Pickup appt",
  notes: "Notes",
};

/** A pre-route in the System log's Line column: "MARSHALLS · Pre-route 3865341". */
const entryText = (v: Pick<PreRouteView, "customerName"> & { record: Pick<PreRouteRecord, "orderNo"> }) =>
  `${v.customerName || "—"} · Pre-route ${v.record.orderNo}`;

function PreRouteScreen({ initialTab }: { initialTab: PreRouteTab }) {
  const navigate = useNavigate();
  const message = useMessage();
  const me = useMe();
  const who = me.data?.authenticated ? me.data.user.displayName || me.data.user.username : "";
  const { data, views, today, calendar, degraded } = usePreRouteViews();
  const writer = useWriter(PRE_ROUTES_KEY, who, today);
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<PreRouteTab>(initialTab);
  const [source, setSource] = useSourceFilter();
  const [search, setSearch] = useState("");
  const [customers, setCustomers] = useState<string[]>([]);
  // My customers (`my-customers.tsx`): on by default for someone with customers; by the order's customer.
  const my = useMyCustomers("pre-route");
  /**
   * Writes in flight (item 6): a new pre-route shown at once as an "Adding…" row, a row being saved
   * or deleted marked so, and the table's loading bar until the reload that shows the result.
   */
  const [adding, setAdding] = useState<PreRouteView[]>([]);
  const [busyRows, setBusyRows] = useState<Readonly<Record<string, string>>>({});
  const [reloading, setReloading] = useState(0);
  const manager = !!data.data?.manager;
  // A Viewer reads the pre-routes: no add form, no Delete, no cell to change; the tabs and search stay.
  const canWrite = !!data.data?.canWrite;
  // Divisions as every page shows them (Admin → Divisions).
  const abbrev = useMemo(() => divisionNamer(data.data?.divisionAbbrevs), [data.data]);
  const tableColumns = useTableColumns(
    "pre-route",
    COLUMNS.filter((c) => manager || c.id !== "value"),
  );

  // Every count and list of the page follows My customers: the tabs, Source, Customer, Ask AI.
  const mineViews = my.mine ? views.filter((v) => isMyPreRoute(v, my.filter)) : views;
  const ofSource = mineViews.filter((v) => inSource(v.record.source, source));
  const filtered = ofSource.filter(
    (v) =>
      preRouteMatches(v, search) &&
      (!customers.length || customers.includes(v.customer.text) || customers.includes(v.customerName)),
  );
  const rows = filtered.filter((v) => inPreRouteTab(v, tab));
  usePreRouteAskAi({
    today,
    tab,
    views: mineViews,
    viewIds: filtered.map((v) => v.record.id),
    mine: my.mine,
    onScreen: rows.length,
    search,
    customers,
    source,
    manager,
  });

  if (data.isPending) return <TableSkeleton />;
  if (data.isError) return <DataError error={data.error} />;

  // The Source filter narrows the Customer list: one option per shown name (Admin → Customers), its
  // customers' names and codes on hover.
  const customerOptions = [...new Set(ofSource.map((v) => v.customer.text).filter(Boolean))].sort().map((c) => {
    const of = ofSource.filter((v) => v.customer.text === c);
    return { value: c, label: c, count: of.length, tip: [...new Set(of.map((v) => v.customer.tip))].sort().join("\n") };
  });

  // The pages are code routes (`pages.tsx`): their paths and `PageSearch` are not in the router's types.
  const goToLine = (line: string) =>
    void navigate({ to: PAGES.worklist.path as never, search: { line } satisfies PageSearch as never });

  /** The pre-routes read again; the table's loading bar shows until they are. */
  const refresh = async () => {
    setReloading((n) => n + 1);
    try {
      await queryClient.invalidateQueries({ queryKey: PRE_ROUTES_KEY });
    } finally {
      setReloading((n) => n - 1);
    }
  };
  /** A row marked while a write and the reload after it run: "Saving…", "Deleting…". */
  const whileBusy = async <T,>(id: string, words: string, job: () => Promise<T>): Promise<T> => {
    setBusyRows((b) => ({ ...b, [id]: words }));
    try {
      return await job();
    } finally {
      setBusyRows((b) => Object.fromEntries(Object.entries(b).filter(([k]) => k !== id)));
    }
  };
  const pending = (v: PreRouteView) => v.record.id.startsWith("adding:") || !!busyRows[v.record.id];

  /** One typed field of a waiting pre-route, saved with its version: someone else's save since is the message. */
  const save = async (v: PreRouteView, field: Editable, value: string) => {
    const old = String(v.record[field] ?? "");
    if (value === old) return true;
    if (v.record.status !== "Waiting")
      return void (await message.error(
        "Can't change it now",
        `This pre-route is ${v.record.status}: it is its record.`,
      ));
    if (field === "appointmentDate") {
      const problem = checkAppointment(value, { today, cancel: v.cancel });
      if (problem) return void (await message.error("Check the pickup appointment", problem));
    }
    try {
      await whileBusy(v.record.id, "Saving…", async () => {
        try {
          await writer.kit.put(
            PRE_ROUTES,
            v.record.id,
            { [field]: value || (field === "appointmentDate" ? null : "") },
            { action: "edit", lineText: entryText(v), changes: fieldChange(field, old, value) },
            { field, value: old },
          );
        } finally {
          await refresh();
        }
      });
      notify("Saved");
      return true;
    } catch (e) {
      if (toastIfReadOnly(e)) return false;
      await message.error(
        lostToAnother(e) ? "Someone else changed this pre-route" : "Couldn't save",
        lostToAnother(e)
          ? "Your change was not saved: it changed after you opened the page (moved, or someone edited it). It now shows the latest — check it, then try again."
          : errorWords(e),
      );
      return false;
    }
  };

  const remove = async (v: PreRouteView) => {
    if (v.record.status !== "Waiting")
      return message.error(
        "Can't delete it",
        `Only a waiting pre-route can be deleted; this one is ${v.record.status}.`,
      );
    const ok = await message.confirm(
      `Delete pre-route ${v.record.orderNo}?`,
      "Its routing ref, appointment and notes go with it.",
      {
        confirmLabel: "Delete",
      },
    );
    if (!ok) return;
    try {
      await whileBusy(v.record.id, "Deleting…", async () => {
        try {
          await writer.kit.remove(PRE_ROUTES, v.record.id, {
            action: "pre-route",
            lineText: entryText(v),
            changes: (["routingRef", "appointmentDate", "notes"] as const).flatMap((f) =>
              fieldChange(f, v.record[f] ?? "", ""),
            ),
            note: `Deleted pre-route ${v.record.orderNo}`,
          });
        } finally {
          await refresh();
        }
      });
      notify(`Deleted pre-route ${v.record.orderNo}`);
    } catch (e) {
      if (toastIfReadOnly(e)) return;
      await message.error("Couldn't delete it", lostToAnother(e) ? "It changed meanwhile (moved?)." : errorWords(e));
    }
  };

  const cells: Record<string, (v: PreRouteView) => ReactNode> = {
    alert: (v) =>
      pending(v) ? (
        <span className="flex items-center gap-1.5 font-medium text-emerald-700">
          <SavingMark />
          {busyRows[v.record.id] ?? "Adding…"}
        </span>
      ) : (
        <AlertCell v={v} />
      ),
    source: (v) => <SourceBadge source={v.record.source} />,
    orderNo: (v) => <span className="font-mono font-semibold">{v.record.orderNo}</span>,
    customer: (v) => (v.customer.text ? <NamedText items={[v.customer]} /> : "—"),
    po: (v) => v.customerPo || "—",
    division: (v) =>
      v.division ? <NamedText items={[{ text: v.divisionAbbrev || v.division, tip: v.division }]} /> : "—",
    dc: (v) => v.dc || "—",
    qty: (v) => <span title={v.qtyTip}>{v.qty !== null ? v.qty.toLocaleString("en-US") : "—"}</span>,
    start: (v) => (v.start ? formatShortDay(v.start) : "—"),
    cancel: (v) => (v.cancel ? formatShortDay(v.cancel) : "—"),
    appointment: (v) => (
      <EditCell v={v} field="appointmentDate" today={today} onSave={save} busy={pending(v)} readOnly={!canWrite} />
    ),
    routingRef: (v) => (
      <EditCell v={v} field="routingRef" today={today} onSave={save} busy={pending(v)} readOnly={!canWrite} />
    ),
    notes: (v) => <EditCell v={v} field="notes" today={today} onSave={save} busy={pending(v)} readOnly={!canWrite} />,
    wh: (v) => v.wh || "—",
    movedTo: (v) =>
      v.movedLineIds.length ? (
        <div className="flex flex-col">
          {v.movedLineIds.map((id) => (
            <Button
              key={id}
              size="xs"
              variant="link"
              className="h-auto justify-start gap-1 px-0"
              onClick={() => goToLine(id)}
            >
              Go to line <ArrowRightIcon className="size-3.5" />
            </Button>
          ))}
        </div>
      ) : (
        "—"
      ),
    movedAt: (v) =>
      v.record.status === "Moved" && v.record.movedAt ? formatDay(localToday(new Date(v.record.movedAt))) : "—",
    value: (v) => (
      <span title={v.valueTip}>{v.value !== null ? `${Math.round(v.value).toLocaleString("en-US")}` : "—"}</span>
    ),
  };
  const columns = tableColumns.shown;
  const counts = Object.fromEntries(
    PRE_ROUTE_TABS.map((t) => [t.id, filtered.filter((v) => inPreRouteTab(v, t.id)).length]),
  );

  return (
    <div className="routing-page space-y-3">
      {/* Reads only (query_pre_routes): nothing here is set from the bar, so no Undo. */}
      <AskAiBar
        mode="pre-route"
        version={`${tab}|${source}|${search}|${customers.join()}|${my.mine}`}
        step={null}
        onBegin={() => {}}
        onEnd={() => {}}
        onUndo={() => {}}
      />
      <div className="flex flex-wrap items-center gap-2">
        <SourcePicker
          value={source}
          counts={{
            A2000: mineViews.filter((v) => v.record.source === "A2000").length,
            Deposco: mineViews.filter((v) => v.record.source === "Deposco").length,
          }}
          onChange={setSource}
        />
        <SearchBox
          value={search}
          onChange={setSearch}
          placeholder="Search pre-routes — order # / SO #, customer, PO, routing ref"
        />
        <FilterPill label="Customer" options={customerOptions} selected={customers} onChange={setCustomers} />
        <MyCustomersChip
          hasMine={my.hasMine}
          mine={my.mine}
          count={my.count}
          labels={my.labels}
          onChange={my.setMine}
        />
        <ColumnsMenu
          columns={tableColumns.ordered}
          order={tableColumns.order}
          hidden={tableColumns.hidden}
          onChange={tableColumns.setHidden}
          onReset={tableColumns.reset}
        />
        <p className="text-muted-foreground ml-auto text-xs">
          Route before the A2000 pick ticket exists · moves to the Worklist by itself
        </p>
      </div>
      {degraded ? (
        <p className="text-xs text-orange-700">
          The holiday calendar is not loaded: alerts count weekends and US holidays only.
        </p>
      ) : null}
      <div className="rounded-md border">
        <div className="flex gap-1 border-b px-2" role="tablist">
          {PRE_ROUTE_TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              title={t.hint}
              onClick={() => setTab(t.id)}
              className={cn(
                "-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm",
                tab === t.id ? "border-primary font-medium" : "text-muted-foreground border-transparent",
              )}
            >
              {t.id === "alerts" ? <span className="size-1.5 rounded-full bg-red-600" /> : null}
              {t.label}
              <span
                className={cn(
                  "rounded px-1.5 text-xs tabular-nums",
                  tab === t.id ? "bg-primary text-primary-foreground" : "bg-muted",
                )}
              >
                {counts[t.id]}
              </span>
            </button>
          ))}
        </div>
        {canWrite ? (
          <AddPreRoute
            today={today}
            who={who}
            preRoutes={data.data.preRoutes}
            abbrev={abbrev}
            writer={writer}
            onGoToLine={goToLine}
            // Shown at once, "Adding…", until the reload brings the saved one (or the save fails).
            onAdding={(input, order) =>
              setAdding((a) => [
                preRouteView(
                  { ...input, id: `adding:${input.orderNo}`, created_at: new Date().toISOString() },
                  { order, pts: NO_PTS, today, holidays: calendar, abbrev },
                ),
                ...a,
              ])
            }
            onAdded={async (orderNos) => {
              try {
                await refresh();
              } finally {
                setAdding((a) => a.filter((v) => !orderNos.includes(v.record.orderNo)));
              }
            }}
          />
        ) : null}
        <div className="overflow-x-auto border-t" data-testid="pre-routes">
          <table className="w-full min-w-[1200px] text-[13px]">
            <thead className="bg-locked text-left text-xs">
              <tr className="[&>th]:px-3 [&>th]:py-2 [&>th]:font-medium">
                {columns.map((c) => (
                  <th key={c.id} className={c.className}>
                    {c.header}
                  </th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody aria-busy={reloading > 0 || undefined}>
              {/* The table reloading after a write: a thin bar under its header (item 6). */}
              <tr aria-hidden>
                <td colSpan={columns.length + 1} className="h-0.5 p-0">
                  {reloading > 0 ? <div className="table-loading" data-testid="pre-routes-loading" /> : null}
                </td>
              </tr>
              {[...adding, ...rows].length === 0 ? (
                <tr>
                  <td colSpan={columns.length + 1} className="text-muted-foreground p-6">
                    {views.length
                      ? "No pre-route here."
                      : canWrite
                        ? "No pre-route yet: add one above."
                        : "No pre-route yet."}
                  </td>
                </tr>
              ) : null}
              {[...adding, ...rows].map((v) => (
                <tr
                  key={v.record.id}
                  aria-busy={pending(v) || undefined}
                  className={cn(
                    "relative border-t align-top [&>td]:px-3 [&>td]:py-2",
                    v.alert?.level === "warn" && "bg-amber-50",
                    v.alert?.level === "late" && "bg-red-50",
                    pending(v) && lineStateClass("saving"),
                  )}
                >
                  {columns.map((c) => (
                    <td key={c.id} className={cn(c.id === "qty" || c.id === "value" ? "text-right tabular-nums" : "")}>
                      {cells[c.id]!(v)}
                    </td>
                  ))}
                  <td>
                    {canWrite && v.record.status === "Waiting" && !pending(v) ? (
                      <Button
                        size="icon-xs"
                        variant="ghost"
                        aria-label={`Delete pre-route ${v.record.orderNo}`}
                        onClick={() => void remove(v)}
                      >
                        <Trash2Icon className="size-3.5" />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <footer className="text-muted-foreground flex flex-wrap gap-x-4 border-t px-3 py-2 text-xs">
          <span>
            <b className="text-foreground">{rows.length}</b> {rows.length === 1 ? "pre-route" : "pre-routes"}
          </span>
          <span>
            Moves to the Worklist by itself when the pick ticket is in the WMS — nobody needs to click · alert when the
            pickup is 2 business days away or less and it still hasn't moved
          </span>
        </footer>
      </div>
    </div>
  );
}

/** The Alert column: the alert, else where it stands; a closed one's red note. */
function AlertCell({ v }: { v: PreRouteView }) {
  if (v.alert)
    return (
      <span
        className={cn(
          "flex items-start gap-1 font-medium",
          v.alert.level === "late" ? "text-red-700" : "text-orange-700",
        )}
      >
        <TriangleAlertIcon className="mt-0.5 size-3.5 shrink-0" />
        {v.alert.text}
      </span>
    );
  if (v.closedNote) return <span className="text-red-700">{v.closedNote}</span>;
  return <span className="text-muted-foreground">{v.record.status === "Waiting" ? v.stageText : "—"}</span>;
}

/** A typed field: a click edits it while the pre-route waits; Enter or leaving saves, Escape puts it back. */
function EditCell({
  v,
  field,
  today,
  onSave,
  busy,
  readOnly,
}: {
  v: PreRouteView;
  field: Editable;
  today: string;
  onSave: (v: PreRouteView, field: Editable, value: string) => Promise<boolean | undefined>;
  /** A write is on the row: shown, not editable. */
  busy?: boolean;
  /** A Viewer: shown, never editable. */
  readOnly?: boolean;
}) {
  const stored = String(v.record[field] ?? "");
  const text = field === "appointmentDate" && stored ? formatDay(stored) : stored;
  const [draft, setDraft] = useState<string | null>(null);
  const message = useMessage();
  const waiting = v.record.status === "Waiting" && !busy && !readOnly;
  if (!waiting || draft === null)
    return (
      <button
        type="button"
        disabled={!waiting}
        title={waiting ? `Change ${LABEL[field]}` : undefined}
        onClick={() => setDraft(field === "appointmentDate" ? (stored ? formatShortDay(stored) : "") : stored)}
        className={cn(
          "w-full text-left",
          waiting && "hover:bg-muted -mx-1 rounded px-1",
          !text && "text-muted-foreground",
        )}
      >
        {text || (waiting ? (field === "routingRef" ? "Add ref" : "—") : "—")}
      </button>
    );
  const commit = async () => {
    let value = draft.trim();
    if (field === "appointmentDate") {
      const parsed = parseDateInput(value, "appointmentDate", today);
      if (!parsed.ok) return void (await message.error("Check the pickup appointment", parsed.message));
      value = parsed.value;
    }
    if (await onSave(v, field, value)) setDraft(null);
  };
  const keys = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") void commit();
    if (e.key === "Escape") setDraft(null);
  };
  if (field === "appointmentDate")
    return (
      <div onKeyDown={keys}>
        <DateField
          value={draft}
          onChange={setDraft}
          today={today}
          autoFocus
          enterOpens={false}
          aria-label={LABEL[field]}
          className="w-28"
        />
      </div>
    );
  return (
    <Input
      autoFocus
      className="h-7 text-[13px]"
      aria-label={LABEL[field]}
      value={draft}
      maxLength={field === "notes" ? 500 : 100}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={keys}
      onBlur={() => void commit()}
    />
  );
}

/**
 * The add form (Figure 30): Source, Order # or PO, Routing ref, Pickup appointment, Notes. What was
 * typed is looked up as it is typed (Figure 29's line): one order as before, or a PO's orders, each
 * ticked when a pre-route is valid for it (`preRouteChoices`). Add checks them again, fresh, and
 * saves one pre-route per order, the same entries on each.
 */
function AddPreRoute({
  today,
  who,
  preRoutes,
  abbrev,
  writer,
  onGoToLine,
  onAdding,
  onAdded,
}: {
  today: string;
  who: string;
  preRoutes: readonly PreRouteRecord[];
  abbrev: DivisionNamer;
  writer: ReturnType<typeof useWriter>;
  onGoToLine: (line: string) => void;
  /** The new pre-route as it is being saved: shown at once. */
  onAdding: (input: Omit<PreRouteRecord, "id">, order: OrderRow) => void;
  /** Saved or not: the table is read again, then the shown ones go. */
  onAdded: (orderNos: readonly string[]) => Promise<void>;
}) {
  const message = useMessage();
  const queryClient = useQueryClient();
  const [source, setSource] = useState<PreRouteSource>("A2000");
  const [orderNo, setOrderNo] = useState("");
  const [routingRef, setRoutingRef] = useState("");
  const [appointment, setAppointment] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const orderRef = useRef<HTMLInputElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const deposco = source === "Deposco";
  // Looked up once typing pauses: a PO is any text, so no length says it is whole.
  const typedNow = orderNo.trim();
  const [query, setQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setQuery(typedNow), 350);
    return () => clearTimeout(t);
  }, [typedNow]);
  const lookup = useQuery({
    queryKey: ["routing", "pre-route-order", source, query],
    queryFn: () => lookupOrder(query, source),
    enabled: !!query && query === typedNow,
    staleTime: 30_000,
  });
  const found = lookup.data && query === typedNow ? lookup.data : undefined;
  const typed = parseDateInput(appointment, "appointmentDate", today);
  const choices = found
    ? preRouteChoices(found, { appointmentDate: typed.ok ? typed.value : "", today, source }, { preRoutes, abbrev })
    : [];
  const single = choices.length === 1 ? choices[0]! : null;
  const many = choices.length > 1;
  // The person's ticks for this source and value: kept while it is read again (after Add too); a
  // new PO looked up starts from its valid ones (`defaultTicks`).
  const lookupKey = `${source}|${query}`;
  const [picked, setPicked] = useState<{ key: string; nos: Set<string> } | null>(null);
  const ticked = found && picked?.key === lookupKey ? picked.nos : defaultTicks(choices);
  const count = choices.filter((c) => c.selectable && ticked.has(c.lookup.orderNo)).length;

  const clear = () => {
    setOrderNo("");
    setRoutingRef("");
    setAppointment("");
    setNotes("");
    setPicked(null);
    orderRef.current?.focus();
  };

  const add = async () => {
    const day = parseDateInput(appointment, "appointmentDate", today);
    if (!day.ok) {
      await message.error("Check the pickup appointment", day.message);
      return dateRef.current?.focus();
    }
    if (!typedNow) return orderRef.current?.focus();
    setBusy(true);
    const tried: string[] = [];
    try {
      // Fresh: the orders, their pick tickets and the pre-routes of those order #s now.
      const fresh = await queryClient.fetchQuery({
        queryKey: ["routing", "pre-route-order", source, typedNow],
        queryFn: () => lookupOrder(typedNow, source),
        staleTime: 0,
      });
      const now = preRouteChoices(fresh, { appointmentDate: day.value, today, source }, { preRoutes, abbrev });
      if (!now.length) {
        await message.error(
          deposco ? "No open Deposco SO with that SO # or PO" : "No A2000 order with that order # or PO",
          `Nothing has ${typedNow}. Check it: ${deposco ? "an e-commerce order" : "a dropship order"}, or one closed more than 30 days ago, isn't offered.`,
        );
        return orderRef.current?.focus();
      }
      // One order: its own message, as before.
      if (now.length === 1 && !now[0]!.check.ok) {
        const c = now[0]!.check;
        await message.error(c.problem.title, c.problem.text);
        return (c.field === "orderNo" ? orderRef : dateRef).current?.focus();
      }
      // Several: those ticked. One no longer valid fails with its reason; the others go on.
      const wanted = now.length === 1 ? now : now.filter((c) => ticked.has(c.lookup.orderNo));
      if (!wanted.length) {
        await message.error("No order ticked", "Tick the orders to pre-route.");
        return;
      }
      const late = wanted.flatMap((c) =>
        !c.check.ok && c.check.field === "appointmentDate"
          ? [{ no: c.lookup.orderNo, text: c.check.problem.text }]
          : [],
      );
      if (late.length) {
        await message.error(
          "Check the pickup appointment",
          late.length === 1
            ? `${late[0]!.no}: ${late[0]!.text}`
            : `It doesn't fit ${late.length} of the ticked orders: ${late.map((l) => l.no).join(", ")}.`,
        );
        return dateRef.current?.focus();
      }
      const failed = wanted.flatMap((c) => (c.check.ok ? [] : [{ no: c.lookup.orderNo, why: c.check.problem.title }]));
      const ready = wanted.filter((c) => c.check.ok);
      const held = ready.flatMap((c) =>
        c.check.ok && c.check.warning ? [{ no: c.lookup.orderNo, w: c.check.warning }] : [],
      );
      if (held.length) {
        const ok = await message.confirm(
          held.length === 1 ? held[0]!.w.title : `${held.length} orders are on hold in A2000`,
          held.length === 1 ? held[0]!.w.text : `${held.map((h) => h.no).join(", ")}. Pre-route them anyway?`,
          { confirmLabel: "Add anyway" },
        );
        if (!ok) return orderRef.current?.focus();
      }
      const saved: string[] = [];
      for (const [i, c] of ready.entries()) {
        const no = c.lookup.orderNo;
        const order = c.lookup.order!;
        if (ready.length > 1) setProgress(`Adding ${i + 1} of ${ready.length}…`);
        const input: Omit<PreRouteRecord, "id"> = {
          source,
          orderNo: no,
          routingRef: routingRef.trim(),
          appointmentDate: day.value || null,
          notes: notes.trim(),
          status: "Waiting",
          movedAt: "",
          movedLineIds: "",
          moveDone: "No",
          addedBy: who,
          orderStatus: "",
          canceledOn: null,
          cancelCode: "",
          ...orderSnapshot(order),
        };
        tried.push(no);
        onAdding(input, order);
        try {
          await writer.kit.create(PRE_ROUTES, input, {
            action: "pre-route",
            lineText: entryText({ customerName: input.customerName, record: { orderNo: no } }),
            changes: (["routingRef", "appointmentDate", "notes"] as const).flatMap((f) =>
              fieldChange(f, "", input[f] ?? ""),
            ),
            note: `Added pre-route ${no} (${deposco ? "Deposco SO" : "A2000 order"}${c.lookup.by === "po" ? `, found by PO ${typedNow}` : ""})`,
          });
          saved.push(no);
        } catch (e) {
          failed.push({ no, why: errorWords(e) });
        }
      }
      const moves = deposco ? "once its line is on the Worklist." : "once its pick ticket is in the WMS.";
      if (now.length === 1) {
        if (saved.length) notify(`Pre-route ${saved[0]} added`, `It moves to the Worklist by itself ${moves}`);
        else await message.error(`Couldn't add pre-route ${failed[0]!.no}`, failed.map((f) => f.why).join("\n"));
        if (!failed.length) clear();
      } else if (failed.length) {
        // The list read again (every saved one tagged), with just the failed ones still valid ticked:
        // Add tries them again; the ones left unticked stay so.
        const after = await queryClient.fetchQuery({
          queryKey: ["routing", "pre-route-order", source, typedNow],
          queryFn: () => lookupOrder(typedNow, source),
          staleTime: 0,
        });
        const again = preRouteChoices(after, { appointmentDate: day.value, today, source }, { preRoutes, abbrev });
        setPicked({
          key: `${source}|${typedNow}`,
          nos: ticksAfterAdd(
            again,
            failed.map((f) => f.no),
          ),
        });
        await message.error(
          `Added ${saved.length} · ${failed.length} failed`,
          failed.map((f) => `${f.no}: ${f.why}`).join("\n"),
        );
      } else {
        notify(
          `Added ${saved.length} pre-route${saved.length === 1 ? "" : "s"}`,
          `Each moves to the Worklist by itself ${moves}`,
        );
        clear();
      }
    } catch (e) {
      if (!toastIfReadOnly(e)) await message.error("Couldn't add the pre-route", errorWords(e));
    } finally {
      setBusy(false);
      setProgress("");
      await onAdded(tried);
    }
  };

  const goTo = single && !single.check.ok ? single.check.problem.goTo : undefined;
  return (
    <div className="space-y-1.5 px-3 py-3">
      <div className="flex flex-wrap items-end gap-2 text-xs">
        <label className="flex flex-col gap-1">
          <span className="font-medium">Source</span>
          <span role="radiogroup" aria-label="Source" className="bg-muted inline-flex rounded-md p-0.5">
            {PRE_ROUTE_ADD_SOURCES.map((id) => [id, id === "A2000" ? "A2000 order" : "Deposco SO"] as const).map(
              ([id, label]) => (
                <button
                  key={id}
                  type="button"
                  role="radio"
                  aria-checked={source === id}
                  className={cn(
                    "rounded px-2.5 py-1",
                    source === id ? "bg-background font-medium shadow-sm" : "text-muted-foreground",
                  )}
                  onClick={() => {
                    setSource(id);
                    orderRef.current?.focus();
                  }}
                >
                  {label}
                </button>
              ),
            )}
          </span>
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-medium">{deposco ? "SO # or PO" : "Order # or PO"}</span>
          <Input
            ref={orderRef}
            className="h-8 w-44 font-mono text-[13px]"
            placeholder={deposco ? "e.g. SO230792 or a PO" : "e.g. 3865341 or a PO"}
            maxLength={40}
            value={orderNo}
            onChange={(e) => setOrderNo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void add()}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-medium">Routing ref / load ID</span>
          <Input
            className="h-8 w-44 text-[13px]"
            placeholder="Load # / RTS# / Conf#"
            maxLength={100}
            value={routingRef}
            onChange={(e) => setRoutingRef(e.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="font-medium">Pickup appointment</span>
          <DateField
            value={appointment}
            onChange={setAppointment}
            today={today}
            inputRef={dateRef}
            className="w-32 [&_input]:h-8 [&_input]:text-[13px]"
            aria-label="Pickup appointment"
          />
        </label>
        <label className="flex min-w-56 flex-1 flex-col gap-1">
          <span className="font-medium">Notes</span>
          <Input
            className="h-8 text-[13px]"
            placeholder="Why pre-routed, container, contact…"
            maxLength={500}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void add()}
          />
        </label>
        <Button className="gap-1" disabled={busy || !typedNow || (many && !count)} onClick={() => void add()}>
          <PlusIcon className="size-4" />
          {many ? `Add ${count} pre-route${count === 1 ? "" : "s"}` : "Add pre-route"}
        </Button>
      </div>
      <div className="min-h-5 text-xs" aria-live="polite">
        {progress ? <span className="text-muted-foreground">{progress}</span> : null}
        {!progress && typedNow && !found && !lookup.isError ? (
          <span className="text-muted-foreground">
            Looking up {typedNow} in {source}…
          </span>
        ) : null}
        {lookup.isError ? (
          <span className="text-red-700">Couldn't look the {deposco ? "SO" : "order"} up: try again.</span>
        ) : null}
        {found && !choices.length ? (
          <span className="flex items-center gap-1 text-red-700">
            <TriangleAlertIcon className="size-3.5" />
            {deposco ? "No open Deposco SO with that SO # or PO" : "No A2000 order with that order # or PO"}
          </span>
        ) : null}
        {single?.check.ok ? (
          <span
            className={cn("flex items-center gap-1", single.check.warning ? "text-orange-700" : "text-emerald-700")}
          >
            <SearchCheckIcon className="size-3.5 shrink-0" />
            <span>
              {single.check.foundParts.map((part, i) => (
                <Fragment key={i}>
                  {i ? " · " : ""}
                  {part.tip ? (
                    <NamedText items={[{ text: part.text, tip: part.tip }]} className="underline decoration-dotted" />
                  ) : (
                    part.text
                  )}
                </Fragment>
              ))}
            </span>
            {single.check.warning ? ` · on hold (${single.lookup.order?.order_hold})` : ""}
          </span>
        ) : null}
        {single && !single.check.ok ? (
          <span className="flex items-center gap-2 text-red-700">
            <TriangleAlertIcon className="size-3.5" />
            {single.check.problem.title}
            {goTo ? (
              <Button size="xs" variant="link" className="h-auto gap-1 px-0" onClick={() => onGoToLine(goTo)}>
                Go to line <ArrowRightIcon className="size-3.5" />
              </Button>
            ) : null}
          </span>
        ) : null}
      </div>
      {many && found ? (
        <PoMatches
          found={found}
          choices={choices}
          source={source}
          ticked={ticked}
          onTick={(nos) => setPicked({ key: lookupKey, nos: new Set(nos) })}
          abbrev={abbrev}
        />
      ) : null}
    </div>
  );
}

/** A PO's orders in the add form, one row each: ticked when a pre-route is valid for it, else why not on hover. */
function PoMatches({
  found,
  choices,
  source,
  ticked,
  onTick,
  abbrev,
}: {
  found: PreRouteOrderMatches;
  choices: readonly PreRouteChoice[];
  source: PreRouteSource;
  ticked: ReadonlySet<string>;
  onTick: (nos: Iterable<string>) => void;
  abbrev: DivisionNamer;
}) {
  const deposco = source === "Deposco";
  const valid = choices.filter((c) => c.selectable).map((c) => c.lookup.orderNo);
  const ofPo = choices.filter((c) => c.lookup.by === "po").length;
  const listedOpen = choices.filter((c) => c.lookup.order?.is_open === 1).length;
  const own = choices.some((c) => c.lookup.by === "order");
  const toggle = (no: string, on: boolean) => {
    const next = new Set(ticked);
    if (on) next.add(no);
    else next.delete(no);
    onTick(next);
  };
  return (
    <div className="rounded-md border" data-testid="pre-route-po-matches">
      <div className="bg-muted/50 flex flex-wrap items-center gap-3 border-b px-2 py-1.5 text-xs">
        <span className="font-medium">
          PO {found.typed} · {ofPo} {ofPo === 1 ? "order" : "orders"}
          {own ? ` · and ${deposco ? "SO" : "order"} # ${found.typed}` : ""}
        </span>
        {found.truncated ? (
          <span className="text-orange-700">
            More have this PO: only these are listed · {found.openTotal} open in all
            {found.openTotal > listedOpen ? `, ${found.openTotal - listedOpen} not listed` : ""}
          </span>
        ) : null}
        <span className="text-muted-foreground">{valid.length} can be pre-routed</span>
        <Button size="xs" variant="link" className="h-auto px-0" onClick={() => onTick(valid)}>
          Select all
        </Button>
        <Button size="xs" variant="link" className="h-auto px-0" onClick={() => onTick([])}>
          None
        </Button>
      </div>
      <div className="max-h-72 overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-background sticky top-0 text-left">
            <tr className="[&>th]:px-2 [&>th]:py-1 [&>th]:font-medium">
              <th className="w-6" />
              <th>{deposco ? "SO #" : "Order #"}</th>
              <th>Customer</th>
              <th>Division</th>
              <th>DC</th>
              {deposco ? <th>Group</th> : null}
              <th className="text-right">Qty</th>
              <th>Start</th>
              <th>Cancel</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {choices.map((c) => {
              const o = c.lookup.order;
              const no = c.lookup.orderNo;
              const division = o?.division ?? "";
              const short = o ? abbrev({ corp: o.corp ?? "", div: o.div ?? "", name: division }) : "";
              const preRouted = c.preRouted;
              return (
                <tr
                  key={no}
                  className={cn("border-t [&>td]:px-2 [&>td]:py-1", !c.selectable && "text-muted-foreground")}
                  title={c.reason || undefined}
                >
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Pre-route ${no}`}
                      checked={c.selectable && ticked.has(no)}
                      disabled={!c.selectable}
                      onChange={(e) => toggle(no, e.target.checked)}
                    />
                  </td>
                  <td className="font-mono whitespace-nowrap">
                    {no}
                    {c.lookup.by === "order" ? (
                      <span className="text-muted-foreground font-sans"> · by {deposco ? "SO" : "order"} #</span>
                    ) : null}
                  </td>
                  <td className="max-w-48 truncate" title={o?.customer_code ?? undefined}>
                    {o?.customer_name || o?.customer_code || "—"}
                  </td>
                  <td>{short !== division ? <NamedText items={[{ text: short, tip: division }]} /> : short}</td>
                  <td>{o?.dc_name || o?.dc_no || ""}</td>
                  {deposco ? (
                    <td className="max-w-64 truncate" title={c.lookup.group ?? undefined}>
                      {c.lookup.group || <span className="text-muted-foreground">no group yet</span>}
                    </td>
                  ) : null}
                  <td className="text-right tabular-nums">{o?.qty != null ? o.qty.toLocaleString("en-US") : ""}</td>
                  <td>{o?.start_date ? formatShortDay(o.start_date) : ""}</td>
                  <td>{o?.cancel_date ? formatShortDay(o.cancel_date) : ""}</td>
                  <td className="whitespace-nowrap">
                    {c.stage}
                    {preRouted ? <span className="text-orange-700"> · already pre-routed</span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
